/**
 * SystemWindowManager - Gestionnaire de fenêtres système
 *
 * Fournit un système de fenêtrage léger au niveau du kernel,
 * indépendant du shell et de deskweb.
 *
 * Les fenêtres ont toujours un z-index >= 100 pour rester
 * au-dessus du shell (z-index: 0).
 */

import { createLogger } from '../utils/debug.js';

const log = createLogger('WinMgr');

// Z-index de base pour les fenêtres (toujours au-dessus du shell)
const BASE_Z_INDEX = 100;

/**
 * Gestionnaire de fenêtres système
 */
export class SystemWindowManager {
  constructor() {
    /** @type {Map<string, WindowState>} */
    this.windows = new Map();

    /** @type {string[]} Focus stack (dernier = focus actuel) */
    this.focusStack = [];

    /** @type {number} */
    this.nextZIndex = BASE_Z_INDEX;

    /** @type {number} */
    this.windowIdCounter = 0;

    /** @type {DragState|null} */
    this.dragState = null;

    /** @type {Function|null} Callback quand focus change */
    this.onFocusChange = null;

    /** @type {Function|null} Callback quand fenêtre ferme */
    this.onWindowClose = null;

    // Bind handlers
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);

    // Injecter les styles CSS
    this.injectStyles();

    log.debug('SystemWindowManager initialized');
  }

  /**
   * Injecte les styles CSS pour les fenêtres
   */
  injectStyles() {
    if (document.getElementById('system-window-styles')) return;

    const style = document.createElement('style');
    style.id = 'system-window-styles';
    style.textContent = `
      .sys-window {
        position: fixed;
        display: flex;
        flex-direction: column;
        background: #1e1e2e;
        border: 1px solid #313244;
        border-radius: 8px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        overflow: hidden;
        min-width: 200px;
        min-height: 150px;
      }

      .sys-window.focused {
        border-color: #89b4fa;
        box-shadow: 0 8px 32px rgba(137, 180, 250, 0.2);
      }

      .sys-window.dragging,
      .sys-window.resizing {
        opacity: 0.9;
        transition: none !important;
      }

      .sys-window-titlebar {
        display: flex;
        align-items: center;
        height: 36px;
        padding: 0 8px;
        background: #181825;
        border-bottom: 1px solid #313244;
        cursor: grab;
        user-select: none;
        flex-shrink: 0;
      }

      .sys-window.dragging .sys-window-titlebar {
        cursor: grabbing;
      }

      .sys-window-title {
        flex: 1;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 13px;
        font-weight: 500;
        color: #cdd6f4;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding: 0 8px;
      }

      .sys-window:not(.focused) .sys-window-title {
        color: #6c7086;
      }

      .sys-window-controls {
        display: flex;
        gap: 8px;
      }

      .sys-window-controls button {
        width: 14px;
        height: 14px;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        font-size: 0;
        transition: opacity 0.15s;
      }

      .sys-window-controls button:hover {
        opacity: 0.8;
      }

      .sys-btn-close {
        background: #f38ba8;
      }

      .sys-btn-minimize {
        background: #f9e2af;
      }

      .sys-btn-maximize {
        background: #a6e3a1;
      }

      .sys-window-content {
        flex: 1;
        overflow: hidden;
        position: relative;
      }

      .sys-window-content iframe {
        width: 100%;
        height: 100%;
        border: none;
      }

      /* Resize handles */
      .sys-resize-handle {
        position: absolute;
        z-index: 10;
      }

      .sys-resize-n {
        top: 0;
        left: 8px;
        right: 8px;
        height: 4px;
        cursor: n-resize;
      }

      .sys-resize-s {
        bottom: 0;
        left: 8px;
        right: 8px;
        height: 4px;
        cursor: s-resize;
      }

      .sys-resize-e {
        right: 0;
        top: 8px;
        bottom: 8px;
        width: 4px;
        cursor: e-resize;
      }

      .sys-resize-w {
        left: 0;
        top: 8px;
        bottom: 8px;
        width: 4px;
        cursor: w-resize;
      }

      .sys-resize-ne {
        top: 0;
        right: 0;
        width: 12px;
        height: 12px;
        cursor: ne-resize;
      }

      .sys-resize-nw {
        top: 0;
        left: 0;
        width: 12px;
        height: 12px;
        cursor: nw-resize;
      }

      .sys-resize-se {
        bottom: 0;
        right: 0;
        width: 12px;
        height: 12px;
        cursor: se-resize;
      }

      .sys-resize-sw {
        bottom: 0;
        left: 0;
        width: 12px;
        height: 12px;
        cursor: sw-resize;
      }

      /* Animation de fermeture */
      .sys-window.closing {
        animation: sys-window-close 0.15s ease-out forwards;
      }

      @keyframes sys-window-close {
        to {
          opacity: 0;
          transform: scale(0.95);
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Génère un ID unique pour une fenêtre
   * @returns {string}
   */
  generateWindowId() {
    return `sys-wnd-${++this.windowIdCounter}-${Date.now().toString(36)}`;
  }

  /**
   * Crée une nouvelle fenêtre
   * @param {Object} options
   * @returns {WindowState}
   */
  createWindow(options = {}) {
    const id = this.generateWindowId();

    // Position par défaut : centré avec léger décalage aléatoire
    const defaultX = Math.floor((window.innerWidth - (options.width || 600)) / 2) + Math.random() * 50 - 25;
    const defaultY = Math.floor((window.innerHeight - (options.height || 400)) / 2) + Math.random() * 50 - 25;

    const win = {
      id,
      pid: options.pid || null,
      title: options.title || 'Fenêtre',
      x: options.x ?? defaultX,
      y: options.y ?? defaultY,
      width: options.width || 600,
      height: options.height || 400,
      minWidth: options.minWidth || 200,
      minHeight: options.minHeight || 150,
      state: 'normal', // normal, minimized, maximized
      focused: false,
      zIndex: this.nextZIndex++,
      resizable: options.resizable !== false,
      closable: options.closable !== false,
      element: null,
      contentElement: null,
      iframe: options.iframe || null,
      savedGeometry: null,
      onClose: options.onClose || null
    };

    // Créer l'élément DOM
    win.element = this.createWindowElement(win);
    document.body.appendChild(win.element);

    // Stocker la fenêtre
    this.windows.set(id, win);

    // Focus sur la nouvelle fenêtre
    this.focusWindow(id);

    log.info(`Window created: ${win.title} (${id})`);

    return win;
  }

  /**
   * Crée l'élément DOM d'une fenêtre
   * @param {WindowState} win
   * @returns {HTMLElement}
   */
  createWindowElement(win) {
    const el = document.createElement('div');
    el.className = 'sys-window';
    el.dataset.windowId = win.id;
    el.style.cssText = `
      left: ${win.x}px;
      top: ${win.y}px;
      width: ${win.width}px;
      height: ${win.height}px;
      z-index: ${win.zIndex};
    `;

    el.innerHTML = `
      <div class="sys-window-titlebar">
        <div class="sys-window-controls">
          <button class="sys-btn-close" title="Fermer"></button>
          <button class="sys-btn-minimize" title="Minimiser"></button>
          <button class="sys-btn-maximize" title="Maximiser"></button>
        </div>
        <span class="sys-window-title">${this.escapeHtml(win.title)}</span>
      </div>
      <div class="sys-window-content"></div>
      ${win.resizable ? this.createResizeHandles() : ''}
    `;

    // Référence au contenu
    win.contentElement = el.querySelector('.sys-window-content');

    // Si on a un iframe, NE PAS la déplacer (cela rechargerait son contenu)
    // À la place, on la positionne en overlay au-dessus de la zone de contenu
    if (win.iframe) {
      this.positionIframeOverContent(win);
    }

    // Configurer les événements
    this.setupWindowEvents(el, win);

    return el;
  }

  /**
   * Positionne l'iframe en overlay au-dessus de la zone de contenu
   * L'iframe reste dans le body mais apparaît visuellement dans la fenêtre
   * @param {WindowState} win
   */
  positionIframeOverContent(win) {
    if (!win.iframe) return;

    const titlebarHeight = 36;

    win.iframe.style.cssText = `
      position: fixed;
      left: ${win.x}px;
      top: ${win.y + titlebarHeight}px;
      width: ${win.width}px;
      height: ${win.height - titlebarHeight}px;
      border: none;
      z-index: ${win.zIndex + 1};
      display: block;
    `;
  }

  /**
   * Met à jour la position de l'iframe quand la fenêtre bouge
   * @param {WindowState} win
   */
  updateIframePosition(win) {
    if (!win.iframe) return;

    const titlebarHeight = 36;

    if (win.state === 'minimized') {
      win.iframe.style.display = 'none';
    } else {
      win.iframe.style.display = 'block';
      win.iframe.style.left = `${win.x}px`;
      win.iframe.style.top = `${win.y + titlebarHeight}px`;
      win.iframe.style.width = `${win.width}px`;
      win.iframe.style.height = `${win.height - titlebarHeight}px`;
      win.iframe.style.zIndex = win.zIndex + 1;
    }
  }

  /**
   * Crée les poignées de redimensionnement
   * @returns {string}
   */
  createResizeHandles() {
    return `
      <div class="sys-resize-handle sys-resize-n" data-resize="n"></div>
      <div class="sys-resize-handle sys-resize-s" data-resize="s"></div>
      <div class="sys-resize-handle sys-resize-e" data-resize="e"></div>
      <div class="sys-resize-handle sys-resize-w" data-resize="w"></div>
      <div class="sys-resize-handle sys-resize-ne" data-resize="ne"></div>
      <div class="sys-resize-handle sys-resize-nw" data-resize="nw"></div>
      <div class="sys-resize-handle sys-resize-se" data-resize="se"></div>
      <div class="sys-resize-handle sys-resize-sw" data-resize="sw"></div>
    `;
  }

  /**
   * Configure les événements d'une fenêtre
   * @param {HTMLElement} el
   * @param {WindowState} win
   */
  setupWindowEvents(el, win) {
    // Titlebar - drag
    const titlebar = el.querySelector('.sys-window-titlebar');
    titlebar.addEventListener('mousedown', (e) => {
      if (e.target.closest('.sys-window-controls')) return;
      this.startDrag(win, e);
    });

    // Double-click titlebar = maximize/restore
    titlebar.addEventListener('dblclick', (e) => {
      if (e.target.closest('.sys-window-controls')) return;
      if (win.state === 'maximized') {
        this.restoreWindow(win.id);
      } else {
        this.maximizeWindow(win.id);
      }
    });

    // Boutons de contrôle
    const closeBtn = el.querySelector('.sys-btn-close');
    const minimizeBtn = el.querySelector('.sys-btn-minimize');
    const maximizeBtn = el.querySelector('.sys-btn-maximize');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeWindow(win.id));
    }
    if (minimizeBtn) {
      minimizeBtn.addEventListener('click', () => this.minimizeWindow(win.id));
    }
    if (maximizeBtn) {
      maximizeBtn.addEventListener('click', () => {
        if (win.state === 'maximized') {
          this.restoreWindow(win.id);
        } else {
          this.maximizeWindow(win.id);
        }
      });
    }

    // Resize handles
    el.querySelectorAll('.sys-resize-handle').forEach(handle => {
      handle.addEventListener('mousedown', (e) => {
        const direction = handle.dataset.resize;
        this.startResize(win, direction, e);
      });
    });

    // Focus on click
    el.addEventListener('mousedown', () => {
      this.focusWindow(win.id);
    });
  }

  /**
   * Démarre le déplacement d'une fenêtre
   * @param {WindowState} win
   * @param {MouseEvent} e
   */
  startDrag(win, e) {
    if (win.state === 'maximized') return;

    this.dragState = {
      type: 'drag',
      window: win,
      startX: e.clientX,
      startY: e.clientY,
      startWinX: win.x,
      startWinY: win.y
    };

    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
    win.element.classList.add('dragging');
  }

  /**
   * Démarre le redimensionnement d'une fenêtre
   * @param {WindowState} win
   * @param {string} direction
   * @param {MouseEvent} e
   */
  startResize(win, direction, e) {
    if (win.state === 'maximized') return;

    this.dragState = {
      type: 'resize',
      window: win,
      direction,
      startX: e.clientX,
      startY: e.clientY,
      startWinX: win.x,
      startWinY: win.y,
      startWidth: win.width,
      startHeight: win.height
    };

    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
    win.element.classList.add('resizing');
  }

  /**
   * Gère le mouvement de la souris pendant drag/resize
   * @param {MouseEvent} e
   */
  handleMouseMove(e) {
    if (!this.dragState) return;

    const { type, window: win, startX, startY } = this.dragState;
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    if (type === 'drag') {
      win.x = this.dragState.startWinX + deltaX;
      win.y = this.dragState.startWinY + deltaY;

      // Garder au moins la titlebar visible
      win.y = Math.max(0, win.y);
      win.x = Math.max(-win.width + 100, Math.min(window.innerWidth - 100, win.x));

      win.element.style.left = `${win.x}px`;
      win.element.style.top = `${win.y}px`;

      // Synchroniser la position de l'iframe
      this.updateIframePosition(win);
    } else if (type === 'resize') {
      this.handleResize(win, deltaX, deltaY);
    }
  }

  /**
   * Gère le redimensionnement selon la direction
   * @param {WindowState} win
   * @param {number} deltaX
   * @param {number} deltaY
   */
  handleResize(win, deltaX, deltaY) {
    const { direction, startWinX, startWinY, startWidth, startHeight } = this.dragState;

    let newX = win.x;
    let newY = win.y;
    let newWidth = win.width;
    let newHeight = win.height;

    // Horizontal
    if (direction.includes('e')) {
      newWidth = Math.max(win.minWidth, startWidth + deltaX);
    }
    if (direction.includes('w')) {
      const potentialWidth = startWidth - deltaX;
      if (potentialWidth >= win.minWidth) {
        newWidth = potentialWidth;
        newX = startWinX + deltaX;
      }
    }

    // Vertical
    if (direction.includes('s')) {
      newHeight = Math.max(win.minHeight, startHeight + deltaY);
    }
    if (direction.includes('n')) {
      const potentialHeight = startHeight - deltaY;
      if (potentialHeight >= win.minHeight) {
        newHeight = potentialHeight;
        newY = startWinY + deltaY;
      }
    }

    // Appliquer
    win.x = newX;
    win.y = newY;
    win.width = newWidth;
    win.height = newHeight;

    win.element.style.left = `${newX}px`;
    win.element.style.top = `${newY}px`;
    win.element.style.width = `${newWidth}px`;
    win.element.style.height = `${newHeight}px`;

    // Synchroniser la position de l'iframe
    this.updateIframePosition(win);
  }

  /**
   * Fin du drag/resize
   */
  handleMouseUp() {
    if (this.dragState) {
      const win = this.dragState.window;
      win.element.classList.remove('dragging', 'resizing');
      this.dragState = null;
    }

    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  /**
   * Met le focus sur une fenêtre
   * @param {string} windowId
   */
  focusWindow(windowId) {
    const win = this.windows.get(windowId);
    if (!win) return;

    // Retirer le focus des autres fenêtres
    this.windows.forEach(w => {
      w.focused = false;
      w.element.classList.remove('focused');
    });

    // Mettre le focus sur cette fenêtre
    win.focused = true;
    win.zIndex = this.nextZIndex++;
    win.element.classList.add('focused');
    win.element.style.zIndex = win.zIndex;

    // Synchroniser le z-index de l'iframe
    this.updateIframePosition(win);

    // Mettre à jour le focus stack
    this.focusStack = this.focusStack.filter(id => id !== windowId);
    this.focusStack.push(windowId);

    // Callback
    if (this.onFocusChange) {
      this.onFocusChange(windowId);
    }

    log.debug(`Window focused: ${win.title}`);
  }

  /**
   * Minimise une fenêtre
   * @param {string} windowId
   */
  minimizeWindow(windowId) {
    const win = this.windows.get(windowId);
    if (!win || win.state === 'minimized') return;

    win.state = 'minimized';
    win.element.style.display = 'none';
    win.focused = false;
    win.element.classList.remove('focused');

    // Cacher l'iframe aussi
    this.updateIframePosition(win);

    // Focus sur la fenêtre précédente
    this.focusPreviousWindow();

    log.debug(`Window minimized: ${win.title}`);
  }

  /**
   * Maximise une fenêtre
   * @param {string} windowId
   */
  maximizeWindow(windowId) {
    const win = this.windows.get(windowId);
    if (!win || win.state === 'maximized') return;

    // Sauvegarder la géométrie
    win.savedGeometry = {
      x: win.x,
      y: win.y,
      width: win.width,
      height: win.height
    };

    win.state = 'maximized';
    win.x = 0;
    win.y = 0;
    win.width = window.innerWidth;
    win.height = window.innerHeight;

    win.element.style.left = '0';
    win.element.style.top = '0';
    win.element.style.width = '100%';
    win.element.style.height = '100%';
    win.element.style.borderRadius = '0';

    // Synchroniser l'iframe
    this.updateIframePosition(win);

    log.debug(`Window maximized: ${win.title}`);
  }

  /**
   * Restaure une fenêtre
   * @param {string} windowId
   */
  restoreWindow(windowId) {
    const win = this.windows.get(windowId);
    if (!win) return;

    if (win.state === 'minimized') {
      win.state = 'normal';
      win.element.style.display = 'flex';
      // Montrer l'iframe aussi
      this.updateIframePosition(win);
      this.focusWindow(windowId);
    } else if (win.state === 'maximized') {
      win.state = 'normal';

      if (win.savedGeometry) {
        win.x = win.savedGeometry.x;
        win.y = win.savedGeometry.y;
        win.width = win.savedGeometry.width;
        win.height = win.savedGeometry.height;
        win.savedGeometry = null;
      }

      win.element.style.left = `${win.x}px`;
      win.element.style.top = `${win.y}px`;
      win.element.style.width = `${win.width}px`;
      win.element.style.height = `${win.height}px`;
      win.element.style.borderRadius = '8px';

      // Synchroniser l'iframe
      this.updateIframePosition(win);
    }

    log.debug(`Window restored: ${win.title}`);
  }

  /**
   * Ferme une fenêtre
   * @param {string} windowId
   */
  closeWindow(windowId) {
    const win = this.windows.get(windowId);
    if (!win) return;

    log.info(`Closing window: ${win.title} (${windowId})`);

    // Cacher l'iframe immédiatement
    if (win.iframe) {
      win.iframe.style.display = 'none';
    }

    // Animation de fermeture
    win.element.classList.add('closing');

    setTimeout(() => {
      // Callback avant suppression
      if (win.onClose) {
        win.onClose(win);
      }

      // Callback global
      if (this.onWindowClose) {
        this.onWindowClose(windowId, win.pid);
      }

      // Retirer l'iframe du DOM (elle est dans le body, pas dans la fenêtre)
      if (win.iframe && win.iframe.parentNode) {
        win.iframe.parentNode.removeChild(win.iframe);
      }

      // Retirer la fenêtre du DOM
      win.element.remove();

      // Retirer de la map et du focus stack
      this.windows.delete(windowId);
      this.focusStack = this.focusStack.filter(id => id !== windowId);

      // Focus sur la fenêtre précédente
      this.focusPreviousWindow();
    }, 150);
  }

  /**
   * Ferme une fenêtre par son PID
   * @param {number} pid
   */
  closeWindowByPid(pid) {
    for (const [windowId, win] of this.windows) {
      if (win.pid === pid) {
        this.closeWindow(windowId);
        return;
      }
    }
  }

  /**
   * Focus sur la fenêtre précédente dans le stack
   */
  focusPreviousWindow() {
    const visibleWindows = this.focusStack.filter(id => {
      const w = this.windows.get(id);
      return w && w.state !== 'minimized';
    });

    if (visibleWindows.length > 0) {
      this.focusWindow(visibleWindows[visibleWindows.length - 1]);
    } else if (this.onFocusChange) {
      // Aucune fenêtre visible, focus retourne au shell
      this.onFocusChange(null);
    }
  }

  /**
   * Met à jour le titre d'une fenêtre
   * @param {string} windowId
   * @param {string} title
   */
  setWindowTitle(windowId, title) {
    const win = this.windows.get(windowId);
    if (!win) return;

    win.title = title;
    const titleEl = win.element.querySelector('.sys-window-title');
    if (titleEl) {
      titleEl.textContent = title;
    }
  }

  /**
   * Récupère une fenêtre par ID
   * @param {string} windowId
   * @returns {WindowState|undefined}
   */
  getWindow(windowId) {
    return this.windows.get(windowId);
  }

  /**
   * Récupère une fenêtre par PID
   * @param {number} pid
   * @returns {WindowState|undefined}
   */
  getWindowByPid(pid) {
    for (const win of this.windows.values()) {
      if (win.pid === pid) return win;
    }
    return undefined;
  }

  /**
   * Liste toutes les fenêtres
   * @returns {WindowState[]}
   */
  getWindows() {
    return Array.from(this.windows.values());
  }

  /**
   * Vérifie si des fenêtres sont ouvertes
   * @returns {boolean}
   */
  hasWindows() {
    return this.windows.size > 0;
  }

  /**
   * Échappe le HTML
   * @param {string} text
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Instance singleton
let instance = null;

/**
 * Obtient l'instance du SystemWindowManager
 * @returns {SystemWindowManager}
 */
export function getSystemWindowManager() {
  if (!instance) {
    instance = new SystemWindowManager();
  }
  return instance;
}
